import type { SelfUserDto, UserSessionDto } from '@_/api-contracts/public-api.ts';
import { DateTime } from 'luxon';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { isApiError } from '@_/utils-frontend';
import { publicApiClient } from '#src/api.ts';

interface AuthState {
  session: UserSessionDto | undefined;
  user: SelfUserDto | undefined;
  locale: string | undefined;
  lastUpdated: DateTime | undefined;

  checkSession: () => Promise<UserSessionDto | undefined>;
  fetchSession: () => Promise<UserSessionDto | undefined>;
  fetchUser: () => Promise<SelfUserDto | undefined>;
  setLocale: (value: string) => void;
  login: (email: string, otp: string) => Promise<UserSessionDto & { isNewUser: boolean }>;
  logout: () => Promise<void>;
}

export type UseAuthStore = ReturnType<typeof createAuthStore>;

export const createAuthStore = () => {
  return create(
    subscribeWithSelector<AuthState>((set, get) => ({
      session: undefined,
      user: undefined,
      locale: undefined,
      lastUpdated: undefined,

      checkSession: async () => {
        const { lastUpdated, session } = get();
        if (!lastUpdated || DateTime.now().diff(lastUpdated, 'minutes').minutes >= 5) {
          return get().fetchSession();
        }
        return session;
      },

      fetchSession: async () => {
        const { locale } = get();

        let session: UserSessionDto | undefined;

        try {
          const resp = await publicApiClient.auth.getSession(undefined, {
            context: { locale }
          });
          session = resp.data;
        } catch (error) {
          if (!isApiError(error) || error.status !== 401) {
            throw error;
          }
        }

        set(state => ({
          ...state,
          session: session ? { ...session } : undefined,
          user: session ? state.user : undefined,
          lastUpdated: DateTime.local()
        }));

        return session;
      },

      fetchUser: async () => {
        const { locale } = get();

        let user: SelfUserDto | undefined;

        try {
          const resp = await publicApiClient.users.getSelf(undefined, {
            context: { locale }
          });
          user = resp.data;
        } catch (error) {
          if (!isApiError(error) || error.status !== 401) {
            throw error;
          }
        }

        set(state => ({
          ...state,
          user: user ? { ...user } : undefined,
          session: user ? state.session : undefined,
          lastUpdated: DateTime.local()
        }));

        return user;
      },

      setLocale: value => {
        if (get().locale !== value) {
          set(state => ({ ...state, locale: value }));
        }
      },

      login: async (email, otp) => {
        const { data: session } = await publicApiClient.auth.verifyOtp({
          body: { email, otp }
        });

        set(state => ({
          ...state,
          session,
          lastUpdated: DateTime.local()
        }));

        return session;
      },

      logout: async () => {
        await publicApiClient.auth.logout();

        set(state => ({
          ...state,
          session: undefined,
          user: undefined,
          lastUpdated: DateTime.local()
        }));
      }
    }))
  );
};
